/* eslint-disable */
/**
 * FAQ Search Bar — reusable block controller.
 *
 * Behaviour:
 *  - Debounced, case-insensitive substring match on every keystroke.
 *  - Multi-token queries (space-separated) match AND-style: all tokens
 *    must appear somewhere in the item's question + answer (+ optional
 *    category title).
 *  - Items that don't match get `data-faq-search-hidden="true"`.
 *  - Categories whose visible item count drops to zero also get hidden;
 *    when that happens the corresponding sidebar link is hidden too,
 *    keeping the sidebar in sync with the visible content.
 *  - When the query clears, all attributes are removed (no destructive
 *    style mutation) and the FAQ reverts to its natural state.
 *
 * Wires onto any FAQ Tabs section on the page — the search bar block
 * can live anywhere (banner, hero, group) as long as the FAQ markup
 * exposes the [data-faq-tabs-item] / [data-faq-tabs-group] hooks.
 */
(function () {
  if (customElements.get('faq-search-bar')) return;

  const HIDDEN_ATTR = 'data-faq-search-hidden';
  const ITEM_SELECTOR = '[data-faq-tabs-item]';
  const GROUP_SELECTOR = '[data-faq-tabs-group]';
  const LINK_SELECTOR = '[data-faq-tabs-link]';

  class FaqSearchBar extends HTMLElement {
    constructor() {
      super();
      this._cleanup = [];
      this._debounceTimer = 0;
    }

    connectedCallback() {
      this._input = this.querySelector('[data-faq-search-input]');
      this._clearBtn = this.querySelector('[data-faq-search-clear]');
      this._status = this.querySelector('[data-faq-search-no-results]');

      if (!this._input) return;

      this._debounceMs = parseInt(this.dataset.faqSearchDebounce, 10);
      if (!isFinite(this._debounceMs) || this._debounceMs < 0) this._debounceMs = 120;

      this._minChars = parseInt(this.dataset.faqSearchMinChars, 10);
      if (!isFinite(this._minChars) || this._minChars < 0) this._minChars = 0;

      this._includeCategoryTitle = this.dataset.faqSearchIncludeCategoryTitle === 'true';

      const onInput = () => {
        this._clearBtn && (this._clearBtn.hidden = !this._input.value);
        clearTimeout(this._debounceTimer);
        if (this._debounceMs > 0) {
          this._debounceTimer = setTimeout(() => this._applyFilter(), this._debounceMs);
        } else {
          this._applyFilter();
        }
      };

      const onClear = () => {
        this._input.value = '';
        if (this._clearBtn) this._clearBtn.hidden = true;
        clearTimeout(this._debounceTimer);
        this._applyFilter();
        this._input.focus();
      };

      const onKeydown = (e) => {
        if (e.key === 'Escape' && this._input.value) {
          e.preventDefault();
          onClear();
        }
      };

      this._input.addEventListener('input', onInput);
      this._input.addEventListener('keydown', onKeydown);
      if (this._clearBtn) this._clearBtn.addEventListener('click', onClear);

      this._cleanup.push(() => this._input.removeEventListener('input', onInput));
      this._cleanup.push(() => this._input.removeEventListener('keydown', onKeydown));
      if (this._clearBtn) {
        this._cleanup.push(() => this._clearBtn.removeEventListener('click', onClear));
      }
    }

    disconnectedCallback() {
      clearTimeout(this._debounceTimer);
      this._cleanup.forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      this._cleanup = [];
    }

    _applyFilter() {
      const rawQuery = (this._input.value || '').trim().toLowerCase();
      const tokens = rawQuery ? rawQuery.split(/\s+/).filter(Boolean) : [];
      const hasQuery = tokens.length > 0 && rawQuery.length >= this._minChars;

      const items = document.querySelectorAll(ITEM_SELECTOR);
      const groups = document.querySelectorAll(GROUP_SELECTOR);

      let totalVisible = 0;

      items.forEach((item) => {
        if (!hasQuery) {
          item.removeAttribute(HIDDEN_ATTR);
          totalVisible++;
          return;
        }

        const haystack = this._haystackForItem(item);
        const matches = tokens.every((t) => haystack.indexOf(t) !== -1);

        if (matches) {
          item.removeAttribute(HIDDEN_ATTR);
          totalVisible++;
        } else {
          item.setAttribute(HIDDEN_ATTR, 'true');
        }
      });

      // Hide groups with zero visible items; hide their sidebar links too.
      groups.forEach((group) => {
        const anchorId = group.getAttribute('data-faq-tabs-group') || '';
        const matchingItems = group.querySelectorAll(ITEM_SELECTOR);

        let groupHasVisible = false;

        if (hasQuery) {
          // Check rendered items first
          for (const it of matchingItems) {
            if (it.getAttribute(HIDDEN_ATTR) !== 'true') {
              groupHasVisible = true;
              break;
            }
          }

          // Optionally rescue the whole group when the category title matches:
          // every item under it becomes visible again so the group renders.
          if (!groupHasVisible && this._includeCategoryTitle) {
            const titleText = this._categoryTitleText(group);
            if (titleText && tokens.every((t) => titleText.indexOf(t) !== -1)) {
              matchingItems.forEach((it) => {
                it.removeAttribute(HIDDEN_ATTR);
                totalVisible++;
              });
              groupHasVisible = true;
            }
          }
        } else {
          groupHasVisible = true;
        }

        if (groupHasVisible) {
          group.removeAttribute(HIDDEN_ATTR);
        } else {
          group.setAttribute(HIDDEN_ATTR, 'true');
        }

        // Keep matching sidebar links in sync — supports any number of
        // FAQ Tabs instances on the page. Anchors come from Liquid's
        // `| handleize` so they're safe to inline as an attribute value.
        if (anchorId) {
          document
            .querySelectorAll(LINK_SELECTOR + '[data-faq-tabs-link="' + anchorId + '"]')
            .forEach((link) => {
              const li = link.closest('li') || link;
              if (groupHasVisible) {
                li.removeAttribute(HIDDEN_ATTR);
              } else {
                li.setAttribute(HIDDEN_ATTR, 'true');
              }
            });
        }
      });

      if (this._status) {
        const hasAnyFaq = items.length > 0;
        this._status.hidden = !hasQuery || !hasAnyFaq || totalVisible > 0;
      }
    }

    _haystackForItem(item) {
      // Cache the lowercased text per item so we don't pay textContent +
      // toLowerCase on every keystroke.
      let text = item.__faqSearchText;
      if (text == null) {
        const question = item.querySelector('.faq-tabs__question');
        const answer = item.querySelector('.faq-tabs__answer');
        text = (
          (question ? question.textContent : '') + ' ' +
          (answer ? answer.textContent : '')
        ).toLowerCase();
        item.__faqSearchText = text;
      }

      if (this._includeCategoryTitle) {
        const group = item.closest(GROUP_SELECTOR);
        if (group) {
          text = text + ' ' + this._categoryTitleText(group);
        }
      }

      return text;
    }

    _categoryTitleText(group) {
      if (group.__faqSearchTitle != null) return group.__faqSearchTitle;
      const attr = group.getAttribute('data-faq-tabs-title') || '';
      const heading = group.querySelector('.faq-tabs__group-title');
      const text = (attr + ' ' + (heading ? heading.textContent : '')).toLowerCase();
      group.__faqSearchTitle = text;
      return text;
    }
  }

  customElements.define('faq-search-bar', FaqSearchBar);
})();
